import type { OptionBlock } from '../../types';

// Page-level mock data for Groupings page
// Export from lib/mockData for actual data

export { mockOptionBlocks, getOptionBlocksByYearGroup } from '../../lib/mockData/curriculum';

// Additional page-specific types for grouping structures

// Sets - ability-based grouping within a year group for a subject
export interface Set {
  id: string;
  name: string; // "Set 1", "Set 2"
  subjectId: string;
  yearGroupId: string;
  setNumber: number;
  targetAbilityLevel: 'high' | 'medium' | 'low' | 'mixed';
  capacity: number;
  currentSize: number;
  teacherId?: string;
  roomId?: string;
}

// Bands - grouping students across multiple subjects
export interface Band {
  id: string;
  name: string; // "Band A", "Band B"
  yearGroupId: string;
  subjects: string[]; // Multiple subjects taught to same group
  capacity: number;
  currentSize: number;
  description?: string;
}

// Mock sets data
export const mockSets: Set[] = [
  // Year 7 Mathematics Sets
  {
    id: 'set-y7-math-1',
    name: 'Set 1',
    subjectId: 'subject-math',
    yearGroupId: 'year-7',
    setNumber: 1,
    targetAbilityLevel: 'high',
    capacity: 28,
    currentSize: 26,
    teacherId: 'teacher-1',
  },
  {
    id: 'set-y7-math-2',
    name: 'Set 2',
    subjectId: 'subject-math',
    yearGroupId: 'year-7',
    setNumber: 2,
    targetAbilityLevel: 'high',
    capacity: 28,
    currentSize: 27,
    teacherId: 'teacher-2',
  },
  {
    id: 'set-y7-math-3',
    name: 'Set 3',
    subjectId: 'subject-math',
    yearGroupId: 'year-7',
    setNumber: 3,
    targetAbilityLevel: 'medium',
    capacity: 28,
    currentSize: 28,
    teacherId: 'teacher-3',
  },
  {
    id: 'set-y7-math-4',
    name: 'Set 4',
    subjectId: 'subject-math',
    yearGroupId: 'year-7',
    setNumber: 4,
    targetAbilityLevel: 'low',
    capacity: 24,
    currentSize: 22,
    teacherId: 'teacher-4',
  },

  // Year 7 English Sets
  {
    id: 'set-y7-english-1',
    name: 'Set 1',
    subjectId: 'subject-english-lang',
    yearGroupId: 'year-7',
    setNumber: 1,
    targetAbilityLevel: 'high',
    capacity: 28,
    currentSize: 25,
    teacherId: 'teacher-5',
  },
  {
    id: 'set-y7-english-2',
    name: 'Set 2',
    subjectId: 'subject-english-lang',
    yearGroupId: 'year-7',
    setNumber: 2,
    targetAbilityLevel: 'high',
    capacity: 28,
    currentSize: 28,
    teacherId: 'teacher-6',
  },
  {
    id: 'set-y7-english-3',
    name: 'Set 3',
    subjectId: 'subject-english-lang',
    yearGroupId: 'year-7',
    setNumber: 3,
    targetAbilityLevel: 'medium',
    capacity: 28,
    currentSize: 27,
    teacherId: 'teacher-7',
  },
  {
    id: 'set-y7-english-4',
    name: 'Set 4',
    subjectId: 'subject-english-lang',
    yearGroupId: 'year-7',
    setNumber: 4,
    targetAbilityLevel: 'low',
    capacity: 24,
    currentSize: 23,
    teacherId: 'teacher-8',
  },
];

// Mock bands data
export const mockBands: Band[] = [
  {
    id: 'band-y9-a',
    name: 'Band A',
    yearGroupId: 'year-9',
    subjects: ['subject-history', 'subject-geography', 'subject-english-lit'],
    capacity: 30,
    currentSize: 28,
    description: 'High ability group across humanities and literature',
  },
  {
    id: 'band-y9-b',
    name: 'Band B',
    yearGroupId: 'year-9',
    subjects: ['subject-history', 'subject-geography', 'subject-english-lit'],
    capacity: 30,
    currentSize: 29,
    description: 'Middle ability group across humanities and literature',
  },
  {
    id: 'band-y9-c',
    name: 'Band C',
    yearGroupId: 'year-9',
    subjects: ['subject-history', 'subject-geography', 'subject-english-lit'],
    capacity: 28,
    currentSize: 27,
    description: 'Foundation group across humanities and literature',
  },
];

// Helper functions
export const getSetsByYearGroup = (yearGroupId: string): Set[] => {
  return mockSets.filter((s) => s.yearGroupId === yearGroupId);
};

export const getSetsBySubject = (subjectId: string): Set[] => {
  return mockSets.filter((s) => s.subjectId === subjectId);
};

export const getBandsByYearGroup = (yearGroupId: string): Band[] => {
  return mockBands.filter((b) => b.yearGroupId === yearGroupId);
};

// Helper to validate set configuration
export const validateSetConfiguration = (sets: Set[]): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check for duplicate set numbers
  const setNumbers = sets.map((s) => s.setNumber);
  const duplicates = setNumbers.filter((num, index) => setNumbers.indexOf(num) !== index);
  if (duplicates.length > 0) {
    errors.push(`Duplicate set numbers found: ${duplicates.join(', ')}`);
  }

  // Check total capacity vs current size
  const totalCapacity = sets.reduce((sum, s) => sum + s.capacity, 0);
  const totalSize = sets.reduce((sum, s) => sum + s.currentSize, 0);
  if (totalSize > totalCapacity) {
    errors.push(`Total students (${totalSize}) exceeds total capacity (${totalCapacity})`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

// Export enriched option blocks with subject names
export interface OptionBlockTableRow extends OptionBlock {
  subjectNames?: string[];
  periodCount?: number;
}

export const enrichOptionBlockForDisplay = (block: OptionBlock): OptionBlockTableRow => {
  return {
    ...block,
    subjectNames: block.subjects.map(() => 'Subject Name'), // Would resolve from store
    periodCount: block.periods?.length || 0,
  };
};
